import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  VITAL_RANGES,
  PAEDIATRIC_VITAL_RANGES,
  getPaediatricAgeGroup,
} from "@/lib/constants";
import SectionPropertiesProps from "@/components/shared/SectionProperties.tsx";

interface Range { min: number; max: number; normal: string; }

interface VitalWarning {
  show: boolean;
}

/**
 * Only flags values that are physically impossible / clear data entry errors.
 * Triggered only on blur — not while typing.
 */
function getVitalWarning(value: string, extremes: { min: number; max: number }): VitalWarning {
  if (!value || value === "") return { show: false };
  const num = parseFloat(value);
  if (isNaN(num)) return { show: false };
  if (num < extremes.min || num > extremes.max) return { show: true };
  return { show: false };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function UnitBox({ unit }: { unit: string }) {
  return (
      <div className="h-7 px-2 bg-muted border border-l-0 rounded-l-none rounded-r-md flex items-center justify-center">
        <span className="text-xs text-muted-foreground font-medium">{unit}</span>
      </div>
  );
}

// No message — just a red border on the input, shown only after blur

function InputWithUnit({
                         unit, value, onChange, title, extremes,
                       }: {
  unit: string;
  value: string;
  onChange: (value: string) => void;
  title?: string;
  extremes?: { min: number; max: number };
}) {
  const [hasBlurred, setHasBlurred] = useState(false);
  const warning = hasBlurred && extremes ? getVitalWarning(value, extremes) : { show: false };

  return (
      <div className="inline-flex w-fit">
        <Input
            type="number"
            min="0"
            step="any"
            value={value}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0)) onChange(v);
            }}
            onBlur={() => setHasBlurred(true)}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
            }}
            title={title}
            className={cn(
                "h-7 w-16 rounded-r-none border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-center bg-card",
                "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                warning.show && "border-destructive/60 bg-destructive/5",
            )}
        />
        <UnitBox unit={unit} />
      </div>
  );
}

const CLINICAL_OPTIONS = ["None", "Mild", "Moderate", "Severe"];

// ─── Extreme value thresholds ─────────────────────────────────────────────────
// These prompt a "please verify" — they don't block input.
// Set wide enough to cover all patient types: neonates, elderly, critically ill.

const EXTREMES = {
  BP_SYS:  { min: 50,   max: 280  },
  BP_DIA:  { min: 20,   max: 160  },
  pulse:   { min: 30,   max: 280  },
  spo2:    { min: 60,   max: 100  },
  weight:  { min: 0.3,  max: 450  }, // premature neonates to morbidly obese
  temp:    { min: 85,   max: 113  }, // hypothermia to hyperthermia (°F)
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function VitalsForm({ patientAgeMonths }: SectionPropertiesProps) {
  const { setValue, watch, formState: { errors } } = useFormContext();

  const vitals = watch("vitals") || {};
  const [bpSys, setBpSys] = useState("");
  const [bpDia, setBpDia] = useState("");
  // Track which fields have been blurred — only show highlight after user leaves field
  const [blurred, setBlurred] = useState<Set<string>>(new Set());
  const markBlurred = (field: string) => setBlurred((prev) => new Set(prev).add(field));

  useEffect(() => {
    const currentBP = vitals.bp || "";
    const expectedBP = bpSys && bpDia ? `${bpSys}/${bpDia}`
        : bpSys ? bpSys : bpDia ? `/${bpDia}` : "";
    if (currentBP !== expectedBP) {
      if (currentBP.includes("/")) {
        const [sys, dia] = currentBP.split("/");
        setBpSys(sys || ""); setBpDia(dia || "");
      } else if (!bpSys && !bpDia && currentBP) {
        setBpSys(currentBP); setBpDia("");
      } else if (!currentBP) {
        setBpSys(""); setBpDia("");
      }
    }
  }, [vitals.bp]);

  const updateBP = (sys: string, dia: string) => {
    const combined = sys && dia ? `${sys}/${dia}` : sys ? sys : dia ? `/${dia}` : "";
    setValue("vitals.bp", combined, { shouldDirty: true });
  };

  const handleBpSysChange = (value: string) => {
    if (value === "" || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
      setBpSys(value); updateBP(value, bpDia);
    }
  };

  const handleBpDiaChange = (value: string) => {
    if (value === "" || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
      setBpDia(value); updateBP(bpSys, value);
    }
  };

  const handleValueChange = (field: string, value: string) => {
    if (value === "" || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0))
      setValue(`vitals.${field}`, value, { shouldDirty: true });
  };

  const handleSelectChange = (field: string, value: string) =>
      setValue(`vitals.${field}`, value, { shouldDirty: true });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
  };

  // ── Range resolution ────────────────────────────────────────────────────────

  const getRange = (key: keyof typeof VITAL_RANGES): Range => {
    if (patientAgeMonths !== undefined && patientAgeMonths < 216) {
      const group = getPaediatricAgeGroup(patientAgeMonths);
      const paedRanges = PAEDIATRIC_VITAL_RANGES[group];
      if (key in paedRanges) return (paedRanges as any)[key];
    }
    return VITAL_RANGES[key];
  };

  // BP warnings — only after blur, only impossible values
  const bpSysWarning: VitalWarning = blurred.has("bpSys")
      ? getVitalWarning(bpSys, EXTREMES.BP_SYS)
      : { show: false };
  const bpDiaWarning: VitalWarning = blurred.has("bpDia")
      ? getVitalWarning(bpDia, EXTREMES.BP_DIA)
      : { show: false };
  const bpWarning: VitalWarning = { show: bpSysWarning.show || bpDiaWarning.show };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
      <div className="space-y-3">

        {/* Main Vitals — inline, each field takes only what it needs */}
        <div className="flex items-start gap-6 flex-nowrap">

          {/* Blood Pressure */}
          <div className="shrink-0">
            <label className="block mb-1 text-xs font-medium text-foreground/70">
              Blood Pressure
            </label>
            <div className="flex">
              <Input
                  type="number" min="0"
                  value={bpSys}
                  onChange={(e) => handleBpSysChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className={cn(
                      "h-7 w-20 rounded-r-none border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-card",
                      "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                      bpSysWarning.show && "border-destructive/60",
                  )}
                  title="Systolic"
                  onBlur={() => markBlurred("bpSys")}
              />
              <div className="w-3 h-7 flex items-center justify-center text-xs text-muted-foreground bg-muted border-t border-b">
                /
              </div>
              <Input
                  type="number" min="0"
                  value={bpDia}
                  onChange={(e) => handleBpDiaChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className={cn(
                      "h-7 w-16 rounded-none border-l-0 border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-card",
                      "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                      bpDiaWarning.show && "border-destructive/60",
                  )}
                  title="Diastolic"
                  onBlur={() => markBlurred("bpDia")}
              />
              <UnitBox unit="mmHg" />
            </div>
          </div>

          {/* Pulse */}
          <div className="shrink-0">
            <label className="block mb-1 text-xs font-medium text-foreground/70">Pulse</label>
            <InputWithUnit
                unit="bpm"
                value={vitals.pulse || ""}
                onChange={(v) => handleValueChange("pulse", v)}
                extremes={EXTREMES.pulse}
            />
          </div>

          {/* SpO2 */}
          <div className="shrink-0">
            <label className="block mb-1 text-xs font-medium text-foreground/70">SpO2</label>
            <InputWithUnit
                unit="%"
                value={vitals.spo2 || ""}
                onChange={(v) => handleValueChange("spo2", v)}
                extremes={EXTREMES.spo2}
            />
          </div>

          {/* Weight — no range warning, patient-specific */}
          <div className="shrink-0">
            <label className="block mb-1 text-xs font-medium text-foreground/70">Weight</label>
            <InputWithUnit
                unit="kg"
                value={vitals.weight || ""}
                onChange={(v) => handleValueChange("weight", v)}
                extremes={EXTREMES.weight}
            />
          </div>

          {/* Temperature */}
          <div className="shrink-0">
            <label className="block mb-1 text-xs font-medium text-foreground/70">Temp</label>
            <InputWithUnit
                unit="°F"
                value={vitals.temp || ""}
                onChange={(v) => handleValueChange("temp", v)}
                extremes={EXTREMES.temp}
            />
          </div>
        </div>

        {/* Clinical Observations — compact inline row */}
        <div className="flex items-center gap-4 pt-2 border-t border-border/40">
          {[
            { field: "edema",   label: "Edema"   },
            { field: "icterus", label: "Icterus" },
            { field: "pallor",  label: "Pallor"  },
          ].map(({ field, label }) => (
              <div key={field} className="flex items-center gap-2">
                <label className="text-xs font-medium text-foreground/70 whitespace-nowrap">
                  {label}
                </label>
                <div className="w-28">
                  <AutocompleteInput
                      value={vitals[field] || ""}
                      onChange={(val) => handleSelectChange(field, val)}
                      options={CLINICAL_OPTIONS}
                      showAllOnFocus={true}
                      dropdownMinWidth={100}
                      inputSize="sm"
                      variant="outlined"
                      containerClassName="h-7 flex items-center w-28"
                  />
                </div>
              </div>
          ))}
        </div>

        {errors.vitals && (
            <div className="text-xs text-destructive flex items-center gap-1.5 p-2 bg-destructive/5 border border-destructive/20 rounded">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Please check vital signs</span>
            </div>
        )}
      </div>
  );
}